import { clsx } from "clsx";
import { Check, ChevronDown } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type ComponentProps,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useCaret } from "./caret.ts";

export { clsx as cx };

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  icon?: ReactNode;
  kbd?: string;
};

export function Button({ variant = "secondary", size = "md", icon, kbd, className, children, type = "button", ...props }: ButtonProps) {
  return (
    <button
      type={type}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50",
        size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-3",
        variant === "primary" && "bg-accent text-white hover:bg-accent-hover",
        variant === "secondary" && "border border-line bg-surface hover:bg-hover",
        variant === "ghost" && "text-muted hover:bg-hover hover:text-text",
        variant === "danger" && "border border-line bg-surface text-danger hover:bg-danger-soft",
        className,
      )}
      {...props}
    >
      {icon}
      {children}
      {kbd && <Kbd className={variant === "primary" ? "border-white/30 bg-white/10 text-white/80" : ""}>{kbd}</Kbd>}
    </button>
  );
}

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={clsx("rounded border border-line bg-surface-2 px-1 font-sans text-[10px] leading-4 text-muted", className)}>{children}</kbd>;
}

const field =
  "w-full rounded-md border border-line bg-surface px-2.5 text-[13px] placeholder:text-faint hover:border-line-strong focus:border-accent focus:outline-none";

export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input className={clsx(field, "h-8", className)} {...props} />;
}

/** A box whose text is upper-cased as it's typed (a currency code), keeping the cursor where the typing is (caret.ts). */
export function UpperInput({ onText, ref, ...props }: Omit<ComponentProps<"input">, "onChange"> & { onText: (text: string) => void }) {
  const caret = useCaret(ref);
  return <Input {...props} ref={caret.ref} onChange={(e) => onText(caret.rewrite(e, (s) => s.toUpperCase()))} />;
}

/**
 * A box that saves when it's left (Enter, Tab, a click elsewhere): `onCommit` with the trimmed text, only if it changed.
 * Esc puts it back and leaves it without saving.
 */
export function CommitInput({
  value,
  onCommit,
  className,
  ...props
}: Omit<ComponentProps<"input">, "value" | "onChange" | "onBlur" | "onKeyDown"> & { value: string; onCommit: (text: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  // Esc's blur must not save: a ref, since the draft it reads is the render's, whatever React has batched.
  const cancelled = useRef(false);
  return (
    <Input
      {...props}
      value={draft ?? value}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (!cancelled.current && draft !== null && draft !== value) onCommit(draft.trim());
        cancelled.current = false;
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          cancelled.current = true;
          e.currentTarget.blur();
        }
      }}
      className={clsx("h-7", className)}
    />
  );
}

/** A small number tile (the item panel's Plan, Sales and Forecast sections): its label, the value, `hint` on hover. */
export function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md bg-surface-2 px-2.5 py-1.5" title={hint}>
      <div className="text-[11px] text-faint">{label}</div>
      <div className="truncate text-[14px] font-semibold">{value}</div>
    </div>
  );
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return <textarea className={clsx(field, "min-h-20 py-1.5", className)} {...props} />;
}

type SelectOption = { value: string; label: string };
/** What a native select's change and blur events carry that callers use. */
type SelectEvent = { target: { value: string } };

export type SelectProps = {
  options: readonly SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (e: SelectEvent) => void;
  onBlur?: (e: SelectEvent) => void;
  /** Keys on the closed dropdown, plus Tab from the open list (so tables can move across). */
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  /** The open list was closed with Escape, a click elsewhere or the same choice, without a change. */
  onDismiss?: () => void;
  /** Opens the list as soon as it shows (spreadsheet cells). */
  defaultOpen?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  title?: string;
  "aria-label"?: string;
};

type Placement = { left: number; width: number; maxHeight: number; top?: number; bottom?: number };

/**
 * A dropdown drawn by the app (not the browser), so its list matches the theme in light and dark. Works like a
 * native select: arrows, Home/End, Enter, Esc, Tab and type-to-jump; long lists get a filter box.
 */
export function Select({
  options,
  value,
  defaultValue,
  onChange,
  onBlur,
  onKeyDown,
  onDismiss,
  defaultOpen,
  autoFocus,
  disabled,
  className,
  ...rest
}: SelectProps) {
  const [own, setOwn] = useState(defaultValue ?? options[0]?.value ?? "");
  const current = value ?? own;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<Placement | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const listId = useId();
  const searchable = options.length > 10;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const selected = options.find((o) => o.value === current);

  const measure = useCallback(() => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    // At least as wide as the button, wider to fit the longest choice (up to 420px); kept on screen below.
    const width = Math.min(Math.max(r.width, 180), 420);
    const below = window.innerHeight - r.bottom - 8;
    const above = r.top - 8;
    const up = below < 220 && above > below;
    setPlace({
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
      width,
      maxHeight: Math.min(320, up ? above : below),
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
    });
  }, []);

  const show = (typed = "") => {
    if (disabled) return;
    measure();
    setQuery(searchable ? typed : "");
    const at = typed && !searchable ? options.findIndex((o) => o.label.toLowerCase().startsWith(typed.toLowerCase())) : -1;
    setActive(Math.max(0, at >= 0 ? at : options.findIndex((o) => o.value === current)));
    setOpen(true);
  };
  const hide = (refocus = true) => {
    setOpen(false);
    setQuery("");
    if (refocus) button.current?.focus();
  };
  const choose = (o: SelectOption | undefined) => {
    if (!o) return;
    hide();
    if (value === undefined) setOwn(o.value);
    if (o.value !== current) onChange?.({ target: { value: o.value } });
    else onDismiss?.();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: opens once, when it first shows
  useEffect(() => {
    if (autoFocus || defaultOpen) button.current?.focus();
    if (defaultOpen) show();
  }, []);

  // While open: follow the button when the page scrolls or resizes; a click elsewhere closes it.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.current?.contains(t) || button.current?.contains(t)) return;
      hide(false);
      onDismiss?.();
    };
    const follow = (e: Event) => {
      if (!pop.current?.contains(e.target as Node)) measure();
    };
    document.addEventListener("mousedown", away, true);
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", measure);
    (pop.current?.querySelector("input") ?? pop.current)?.focus();
    return () => {
      document.removeEventListener("mousedown", away, true);
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", measure);
    };
  });

  // A list wider than its button can run off the right edge: pull it back on screen before it paints.
  useLayoutEffect(() => {
    const el = pop.current;
    if (!open || !el) return;
    const r = el.getBoundingClientRect();
    const over = r.right - (window.innerWidth - 8);
    if (over > 0) el.style.left = `${Math.max(8, r.left - over)}px`;
  });

  useEffect(() => {
    if (open) pop.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  // Focus leaving the button and its list counts as one blur, like a native select's.
  const leave = (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && (button.current?.contains(next) || pop.current?.contains(next))) return;
    if (open) hide(false);
    onBlur?.({ target: { value: current } });
  };

  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp" || (e.key === "Enter" && !onKeyDown)) {
      e.preventDefault();
      show();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      show(e.key);
    } else onKeyDown?.(e);
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = (n: number) => setActive((a) => Math.max(0, Math.min(shown.length - 1, a + n)));
    const typing = (e.target as HTMLElement).tagName === "INPUT";
    if (e.key === "ArrowDown") step(1);
    else if (e.key === "ArrowUp") step(-1);
    else if (e.key === "PageDown") step(8);
    else if (e.key === "PageUp") step(-8);
    else if (e.key === "Home" && !typing) setActive(0);
    else if (e.key === "End" && !typing) setActive(shown.length - 1);
    else if (e.key === "Enter") choose(shown[active]);
    else if (e.key === "Escape") {
      hide();
      onDismiss?.();
    } else if (e.key === "Tab") {
      // Closes without changing; a table moves to the next cell as it would from a native select.
      hide();
      const b = button.current;
      if (b && onKeyDown)
        onKeyDown({
          key: "Tab",
          shiftKey: e.shiftKey,
          currentTarget: b,
          target: b,
          preventDefault() {},
          stopPropagation() {},
        } as unknown as KeyboardEvent<HTMLButtonElement>);
    } else if (!typing && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      const from = active + 1;
      const hit = [...shown.slice(from), ...shown.slice(0, from)].find((o) => o.label.toLowerCase().startsWith(e.key.toLowerCase()));
      if (hit) setActive(shown.indexOf(hit));
    } else {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        value={current}
        disabled={disabled}
        onClick={() => (open ? hide() : show())}
        onKeyDown={onButtonKey}
        onBlur={leave}
        className={clsx(field, "flex h-8 items-center gap-1.5 text-left disabled:pointer-events-none disabled:opacity-50", className)}
        {...rest}
      >
        <span className={clsx("min-w-0 flex-1 truncate", !selected && "text-faint")}>{selected?.label ?? ""}</span>
        <ChevronDown className="size-3.5 shrink-0 text-faint" />
      </button>
      {open &&
        place &&
        createPortal(
          <div
            ref={pop}
            // A layer of its own over the page: a Popover it's inside counts clicks here as its own (Popover.tsx)
            data-floating=""
            tabIndex={-1}
            onKeyDown={onListKey}
            onBlur={leave}
            // Clicks inside keep focus where it is, so choosing isn't seen as leaving.
            onMouseDown={(e) => {
              if ((e.target as HTMLElement).tagName !== "INPUT") e.preventDefault();
            }}
            style={{ left: place.left, top: place.top, bottom: place.bottom, minWidth: place.width, maxWidth: 420, maxHeight: place.maxHeight }}
            className="fixed z-50 flex flex-col overflow-hidden rounded-lg border border-line bg-surface text-[13px] text-text shadow-pop focus:outline-none"
          >
            {searchable && (
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                placeholder="Filter…"
                aria-label="Filter the choices"
                className="m-1 h-7 shrink-0 rounded-md border border-line bg-surface-2 px-2 placeholder:text-faint focus:border-accent focus:outline-none"
              />
            )}
            <div id={listId} role="listbox" className="min-h-0 overflow-y-auto py-1">
              {shown.map((o, i) => (
                <div
                  key={o.value}
                  data-index={i}
                  role="option"
                  tabIndex={-1}
                  aria-selected={o.value === current}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(o)}
                  className={clsx("flex cursor-default items-center gap-2 px-2.5 py-1.5", i === active && "bg-hover")}
                >
                  <Check className={clsx("size-3.5 shrink-0 text-accent", o.value !== current && "invisible")} />
                  <span className="min-w-0 truncate">{o.label || "—"}</span>
                </div>
              ))}
              {!shown.length && <div className="px-2.5 py-1.5 text-faint">No matches</div>}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

const TONES = {
  neutral: "bg-surface-2 text-muted",
  accent: "bg-accent-soft text-accent-text",
  ok: "bg-ok-soft text-ok",
  warn: "bg-warn-soft text-warn",
  danger: "bg-danger-soft text-danger",
};

export function Badge({ tone = "neutral", children }: { tone?: keyof typeof TONES; children: ReactNode }) {
  return <span className={clsx("inline-flex h-5 items-center rounded px-1.5 text-[11px] font-medium whitespace-nowrap", TONES[tone])}>{children}</span>;
}

export function EmptyState({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
      {icon && <div className="mb-1 text-faint">{icon}</div>}
      <div className="text-sm font-medium">{title}</div>
      {children && <div className="max-w-md text-muted">{children}</div>}
    </div>
  );
}

/** Small square product photo for tables and pickers. */
export function Thumb({ src, size = 24, className }: { src: string | null | undefined; size?: number; className?: string }) {
  const style = { width: size, height: size };
  if (!src) return <span className={clsx("inline-block shrink-0 rounded bg-surface-2", className)} style={style} />;
  // Shopify's CDN resizes on request; ask for 2x the shown size.
  const url = src.includes("cdn.shopify.com") ? `${src}${src.includes("?") ? "&" : "?"}width=${size * 2}` : src;
  return <img src={url} alt="" loading="lazy" className={clsx("shrink-0 rounded object-cover", className)} style={style} />;
}

type NamedItem = { name: string; shopify_product_title: string; shopify_variant_title: string };

/**
 * A variant before its product ("Wired / Black", then "GC Ultimate 2 Controller" muted): what tells boxes apart. `name`
 * is the name as saved (a PO line's, as printed on the PO): split where its product's title ends, else shown whole.
 */
export function nameParts(name: string, item: NamedItem | null | undefined): { first: string; rest: string } {
  const product = item?.shopify_product_title ?? "";
  const variant = item?.shopify_variant_title ?? "";
  if (!item || !variant || variant === "Default Title") return { first: name, rest: "" };
  if (product && name.startsWith(`${product} - `)) return { first: name.slice(product.length + 3), rest: product };
  return !name || name === item.name ? { first: variant, rest: product } : { first: name, rest: "" };
}

/** A line's item name, variant first and the product muted after it; one line, cut at the end. */
export function ItemName({ name, item, className }: { name: string; item: NamedItem | null | undefined; className?: string }) {
  const { first, rest } = nameParts(name, item);
  return (
    <span className={clsx("min-w-0 truncate", className)} title={rest ? `${first} · ${rest}` : first}>
      {first}
      {rest && <span className="text-muted"> · {rest}</span>}
    </span>
  );
}
